/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { TouchableOpacity, ScrollView, View, ViewStyle } from "react-native";
import ReactPlayer from "react-player";
import parse from "html-react-parser";
import SwipeableViews from "react-swipeable-views";
import Icon from "react-icons-kit";
import { calendarO } from "react-icons-kit/fa/calendarO";
import { shareAlt } from "react-icons-kit/fa/shareAlt";
import "rodal/lib/rodal.css";
import { ic_check } from "react-icons-kit/md/ic_check";
import { CopyToClipboard } from "react-copy-to-clipboard";
import qs from "qs";
import Toggle from "react-toggle";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState, VideoInfoType } from "store/types";

import {
  Button,
  Text,
  UserCell,
  Transcription,
  LoadingView,
  KudoView,
} from "UpduoLibrary";
import {
  skaleBlack,
  skaleCellGreen,
  skaleGreen,
  skaleLightGray,
  skaleWhite,
  skaleWebTextGray,
  skaleWebTextLightGray,
  skaleWebFillStormy,
  skaleWebFillLightGray,
  upduoPrimaryBlue,
  upduoDarkGray,
} from "config/UpduoColors";
import { isNotNil, processSkillString, getNodes } from "config/Utilities";
import {
  SkaleSessionType,
  useEditSkalesessionMutation,
  useGetKudosQuery,
  useLogSessionViewMutation,
} from "graphql/internal/generated";

import * as UpduoAnalytics from "config/UpduoAnalytics";

function VideoUserCell({
  identity,
  videoInfo,
  callback,
}: {
  identity: "teacher" | "learner";
  videoInfo: NonNullable<VideoInfoType>;
  callback: () => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 18,
        paddingVertical: 8,
      }}
    >
      <UserCell
        user={videoInfo[identity]}
        isVideo={true}
        title={identity.toUpperCase()}
        callback={callback}
        width={110 /* TODO */}
        isGraphql={true}
        orgType={"C"}
        hasTooltip={true}
      />
      <View style={{ flex: 1 }} />
    </View>
  );
}

function VideoUserSteps({ videoInfo, currentStepIndex, isReady, identity }) {
  if (!videoInfo.sessionSteps) {
    return null;
  }
  // Perform string replacements for all relevant step card fields (requires mutable object)
  const roles = ["teacher", "learner"];
  const stringTypes = ["heading", "detail"];
  // Use JSON.parse and JSON.stringify to generate mutable object
  const mutableSessionSteps = JSON.parse(
    JSON.stringify(videoInfo.sessionSteps)
  );
  for (let i = 0; i < videoInfo.sessionSteps.length; i++) {
    for (const role of roles) {
      for (const stringType of stringTypes) {
        if (mutableSessionSteps?.[i]?.[role]?.[stringType]) {
          mutableSessionSteps[i][role][stringType] = processSkillString(
            mutableSessionSteps[i][role][stringType],
            videoInfo.teacher.firstName,
            videoInfo.learner.firstName,
            videoInfo.skill.titleTeacher,
            videoInfo.skill.titleLearner,
            videoInfo.skill.name,
            role === "teacher",
            videoInfo?.organization?.name || ""
          );
        }
      }
    }
  }
  return (
    <SwipeableViews
      disabled={false}
      index={currentStepIndex}
      style={{ height: "100%" }}
      className={"expand-child"}
    >
      {mutableSessionSteps.map((step, stepIndex) => {
        return (
          <View
            key={stepIndex}
            style={{
              height: "100%",
              padding: 20,
              flexGrow: 1,
              justifyContent: "space-between",
              overflow: "hidden",
            }}
          >
            <ScrollView>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <Text
                  style={{
                    color: skaleBlack,
                    fontWeight: "bold",
                    fontSize: 20,
                  }}
                >
                  {`Card ${stepIndex + 1}/${mutableSessionSteps.length}`}
                </Text>
                <Text style={{ color: skaleBlack, fontSize: 14 }}>
                  {`about ${step.minutes} min${step.minutes > 1 ? "s" : ""}`}
                </Text>
              </View>
              <View>
                <Text>{step[identity].heading}</Text>
                {step[identity].detail ? (
                  <View style={{ marginTop: 30 }}>
                    <Text style={{ color: skaleWebTextLightGray }}>
                      {"Detail:"}
                    </Text>
                    <div style={{ fontSize: 12, lineHeight: "12px" }}>
                      {parse(step[identity].detail)}
                    </div>
                  </View>
                ) : null}
              </View>
            </ScrollView>
            <View
              style={{
                alignSelf: "center",
                paddingTop: 20,
              }}
            >
              <Button
                width={170}
                height={60}
                hoverBorderWidth={1}
                textColor={isReady ? skaleGreen : skaleWhite}
                color={isReady ? skaleCellGreen : skaleLightGray}
                text={
                  stepIndex === mutableSessionSteps.length - 1
                    ? "END LESSON"
                    : "READY"
                }
              />
            </View>
          </View>
        );
      })}
    </SwipeableViews>
  );
}

interface ArchiveVideoProps {
  standalone?: boolean;
  videoInfo: NonNullable<VideoInfoType>;
  url?: string;
  onUpdateSession?: (
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => void;
}

export default function ArchiveVideo({
  standalone = false,
  videoInfo,
  url = "",
  onUpdateSession,
}: ArchiveVideoProps) {
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const player: any = useRef(null);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [teacherIsReady, setTeacherIsReady] = useState(false);
  const [learnerIsReady, setLearnerIsReady] = useState(false);
  const [doAllowSharing, setDoAllowSharing] = useState(
    videoInfo ? videoInfo.allowSharing : false
  );
  const [isSessionComplete, setIsSessionComplete] = useState(
    videoInfo ? !videoInfo.isCancelled : false
  );
  const [copied, setCopied] = useState(false);
  const [playerIsReady, setPlayerIsReady] = useState(false);

  const [previousProgressSeconds, setPreviousProgressSeconds] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);

  const [editSkalesession, { loading: sessionEditInProgress }] =
    useEditSkalesessionMutation({
      onCompleted: ({ editSkalesession }) => {
        const allowSharing = editSkalesession.allowSharing;
        if (isNotNil(allowSharing)) {
          setDoAllowSharing(allowSharing);
        }
        const isCancelled = editSkalesession.isCancelled;
        if (isNotNil(isCancelled)) {
          setIsSessionComplete(!isCancelled);
        }
        onUpdateSession &&
          onUpdateSession({
            allowSharing: editSkalesession.allowSharing || undefined,
            isCancelled: editSkalesession.isCancelled || undefined,
            masteryEdited: editSkalesession.masteryEdited || undefined,
          });
      },
      onError: (error) => {
        console.error("ERROR editSkalesession", error);
        UpduoAnalytics.logEvent("app-archivevideo-editskalesession-error", {
          error,
        });
      },
    });

  const [logSessionView, {}] = useLogSessionViewMutation({
    onCompleted: ({ logSessionView }) => {
      UpduoAnalytics.logEvent(
        "app-archivevideo-logsessionview-success",
        logSessionView
      );
    },
    onError: (error) => {
      console.error("ERROR logSessionView", error);
      UpduoAnalytics.logEvent("app-archivevideo-logsessionview-error", {
        error,
      });
    },
  });

  const queryParams = qs.parse(url.split("?")[1]);

  useEffect(() => {
    if (playerIsReady && queryParams.start) {
      player.current.seekTo(queryParams.start, "seconds");
    }
  }, [playerIsReady, queryParams.start]);

  const _handleChangeIsSessionComplete = () => {
    editSkalesession({
      variables: {
        editSkalesessionMutationRequest: {
          skalesessionId: videoInfo.id,
          isCancelled: isSessionComplete,
        },
      },
    });
  };

  const _handleChangeSharing = () => {
    editSkalesession({
      variables: {
        editSkalesessionMutationRequest: {
          skalesessionId: videoInfo.id,
          allowSharing: !doAllowSharing,
        },
      },
    });
  };

  const _handleVideoOnPlay = useCallback(() => {
    if (previousProgressSeconds === 0) {
      logSessionView({
        variables: {
          logSessionViewRequest: {
            sessionId: videoInfo.id,
          },
        },
      });
    }
  }, [previousProgressSeconds]);

  const _handleVideoOnProgress = (onProgressObject) => {
    if (
      player &&
      queryParams.stop &&
      onProgressObject.playedSeconds > queryParams.stop
    ) {
      player.current.player.player.pause();
      if (queryParams.start) {
        player.current.seekTo(queryParams.start, "seconds");
      }
    }
    if (!videoInfo.sessionSteps) {
      return;
    }
    const currentSeconds = onProgressObject.playedSeconds;
    let currentStepIndex = 0;
    let teacherIsReady = false;
    let learnerIsReady = false;
    let teacherIsReadyCandidate = false;
    let learnerIsReadyCandidate = false;
    for (let i = videoInfo.sessionSteps.length - 1; i >= 0; i--) {
      teacherIsReadyCandidate =
        currentSeconds >= videoInfo.stepsReadyTeacher[i];
      learnerIsReadyCandidate =
        currentSeconds >= videoInfo.stepsReadyLearner[i];
      if (teacherIsReadyCandidate && learnerIsReadyCandidate) {
        currentStepIndex = i + 1;
        break;
      } else {
        // Keep track whenever only one user is Ready
        teacherIsReady = teacherIsReadyCandidate;
        learnerIsReady = learnerIsReadyCandidate;
      }
    }
    setCurrentStepIndex(currentStepIndex);
    setTeacherIsReady(teacherIsReady);
    setLearnerIsReady(learnerIsReady);

    // ReactPlayer's onProgress callback is called after every second of view time,
    // and also after each scrubbing/seeking action.
    // For roughly every 5 seconds of true session viewing time,
    // the client should send the server a logSessionView request.
    // If the user is scrubbing the video by >=2 seconds, we don't want to
    // count the scrubbed time period as true session viewing time.
    const latestSecondsDelta = currentSeconds - previousProgressSeconds;
    if (Math.abs(latestSecondsDelta) < 2) {
      const newPlayedSeconds = playedSeconds + latestSecondsDelta;
      if (newPlayedSeconds >= 5) {
        logSessionView({
          variables: {
            logSessionViewRequest: {
              sessionId: videoInfo.id,
            },
          },
        });
        setPlayedSeconds(0);
      } else {
        setPlayedSeconds(newPlayedSeconds);
      }
    }
    setPreviousProgressSeconds(currentSeconds);
  };

  if (!videoInfo) return null;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  const createdDate = new Date(videoInfo.created);
  const dateTimeString = createdDate.toLocaleDateString("en-US", options);
  const [dayString, yearAndTimeString] = dateTimeString.split(", ");
  const dateString = `${dayString}, ${yearAndTimeString}`;
  const analyticsObject = standalone ? { standalone: true } : null;
  const videoId = videoInfo.id;
  const urlString = `${process.env.PUBLIC_URL}/#/share/${videoId}`;
  const isOrganizationMember =
    selfObject?.organizationId &&
    selfObject?.organizationId === videoInfo?.organization?.id;
  const isOrganizationMaintenanceMember =
    isOrganizationMember && selfObject?.storeName === "Maintenance";

  const allowDownload =
    process.env.REACT_APP_CONFIG === "staging" ||
    selfObject?.storeName === "Maintenance";

  const sessionCompleteEditor = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginVertical: 5,
      }}
    >
      <Toggle
        checked={isSessionComplete}
        disabled={!isOrganizationMember || sessionEditInProgress}
        onChange={_handleChangeIsSessionComplete}
      />
      <Text
        style={{
          color: skaleWebTextGray,
          fontSize: 14,
          marginLeft: 5,
        }}
      >
        {`Session ${isSessionComplete ? "Completed" : "Incomplete"}`}
      </Text>
    </View>
  );

  const doAllowSharingEditor = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginVertical: 5,
      }}
    >
      <Toggle
        checked={doAllowSharing}
        disabled={!isOrganizationMaintenanceMember || sessionEditInProgress}
        onChange={_handleChangeSharing}
      />
      <Text
        style={{
          color: skaleWebTextGray,
          fontSize: 14,
          marginLeft: 5,
        }}
      >
        {"Allow Sharing"}
      </Text>
    </View>
  );

  const menuBar = (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <View
        style={{
          flexDirection: "row",
        }}
      >
        <View
          style={{
            padding: 10,
            justifyContent: "center",
            marginRight: 30,
            backgroundColor: skaleWebFillLightGray,
            borderRadius: 10,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div
            style={{
              color: skaleWebFillStormy,
              marginRight: 10,
              marginTop: -3,
            }}
          >
            <Icon size={20} icon={calendarO} />
          </div>

          <Text
            style={{
              color: skaleWebTextGray,
              fontSize: 14,
            }}
          >
            {dateString}
          </Text>
        </View>
        <View style={{ justifyContent: "center" }}>
          {sessionCompleteEditor}
        </View>
      </View>
      <View style={{ flexDirection: "row" }}>
        {isOrganizationMaintenanceMember ? doAllowSharingEditor : null}
        <View style={{ justifyContent: "center", marginLeft: 30 }}>
          <TouchableOpacity>
            <CopyToClipboard
              text={url ? url : urlString}
              onCopy={() => setCopied(true)}
              options={{ format: "text/plain" }}
            >
              <div
                style={{
                  color: copied ? "green" : "black",
                }}
              >
                <Icon size={20} icon={copied ? ic_check : shareAlt} />
                <Text>{copied ? " Copied!" : " Share link"}</Text>
              </div>
            </CopyToClipboard>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={{
        width: "100%",
        height: "100%",
        padding: 20,
      }}
    >
      <Text
        style={{
          fontSize: 20,
          fontWeight: "bold",
          paddingBottom: 20,
          color: skaleWebTextGray,
        }}
      >
        {`Session: ${videoInfo.skill.name}`}
      </Text>
      {menuBar}
      <View
        style={{
          paddingTop: 20,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
          flexGrow: 1,
          flexShrink: 1,
        }}
      >
        <VideoParticipantColumn
          videoInfo={videoInfo}
          identity={"teacher"}
          currentStepIndex={currentStepIndex}
          isReady={teacherIsReady}
        />
        <View
          style={{
            display: "flex",
            flexDirection: "column",
            paddingRight: 10,
            paddingLeft: 10,
            width: "60%",
            flexShrink: 1,
          }}
        >
          <View style={{ flexShrink: 1 }}>
            <ReactPlayer
              width={"unset"}
              height={"unset"}
              style={{
                maxHeight: "100%",
                backgroundColor: "#000",
              }}
              ref={player}
              url={videoInfo.archiveUrl ? videoInfo.archiveUrl : undefined}
              controls={true}
              onReady={() => {
                setPlayerIsReady(true);
              }}
              onStart={() => {
                UpduoAnalytics.logEvent("user-video-play", analyticsObject);
              }}
              onPause={() => {
                UpduoAnalytics.logEvent("user-video-pause", analyticsObject);
              }}
              config={{
                file: {
                  attributes: {
                    controlsList: allowDownload ? "" : "nodownload",
                  },
                },
              }}
              onPlay={_handleVideoOnPlay}
              onProgress={_handleVideoOnProgress}
            />
          </View>
          {selfObject?.organizationId ? (
            <TranscriptKudosSwitcher
              showTranscript={
                selfObject.organizationShowTranscription &&
                videoInfo?.hasTranscript &&
                videoInfo?.transcriptionUrl &&
                videoInfo?.transcriptJson
              }
              videoInfo={videoInfo}
            />
          ) : null}
        </View>
        <VideoParticipantColumn
          videoInfo={videoInfo}
          identity={"learner"}
          currentStepIndex={currentStepIndex}
          isReady={learnerIsReady}
        />
      </View>
    </View>
  );
}

function TranscriptKudosSwitcher({
  showTranscript,
  videoInfo,
}: {
  showTranscript: boolean;
  videoInfo: NonNullable<VideoInfoType>;
}) {
  enum PanelType {
    KUDOS,
    TRANSCRIPT,
  }
  const [panel, setPanel] = useState<PanelType>(PanelType.KUDOS);

  const titleStyle = {
    fontWeight: "bold" as const,
    fontSize: 14,
    lineHeight: 19,
    marginTop: 20,
    marginBottom: 10,
  };

  const activeTabStyle: ViewStyle = {
    borderBottomWidth: 3,
    borderStyle: "solid",
    borderBottomColor: upduoPrimaryBlue,
  };

  const tabStyle: ViewStyle = {
    marginRight: 20,
  };

  return (
    <View
      style={{
        height: 200,
        flexShrink: 0,
        flexGrow: 1,
      }}
    >
      <View style={{ display: "flex", flexDirection: "row" }}>
        {showTranscript && videoInfo.transcriptJson ? (
          <>
            <TouchableOpacity
              onPress={() => setPanel(PanelType.KUDOS)}
              style={[
                tabStyle,
                panel === PanelType.KUDOS ? activeTabStyle : null,
              ]}
            >
              <Text style={titleStyle}>{"Kudos"}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setPanel(PanelType.TRANSCRIPT)}
              style={[
                tabStyle,
                panel === PanelType.TRANSCRIPT ? activeTabStyle : null,
              ]}
            >
              <Text style={titleStyle}>{"Transcript"}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <View style={tabStyle}>
            <Text style={titleStyle}>{"Kudos"}</Text>
          </View>
        )}
      </View>
      {panel === PanelType.KUDOS ? (
        <SessionKudosView sessionId={videoInfo.id} />
      ) : (
        <Transcription transcriptJson={videoInfo.transcriptJson} />
      )}
    </View>
  );
}

function SessionKudosView({ sessionId }: { sessionId: string }) {
  const { data, loading, error } = useGetKudosQuery({
    fetchPolicy: "network-only",
    variables: {
      getKudosRequest: {
        session: sessionId,
      },
    },
  });

  if (loading) {
    return <LoadingView />;
  }

  if (!data) {
    return (
      <Text
        style={{ marginVertical: 10 }}
      >{`Error loading kudos. Error message: ${
        error?.message ?? "none"
      }`}</Text>
    );
  }

  const kudos = getNodes(data.kudos2);

  if (kudos.length === 0) {
    return (
      <Text style={{ marginVertical: 10, color: upduoDarkGray }}>
        {"No kudos were sent for this session"}
      </Text>
    );
  }

  return (
    <ScrollView>
      {kudos.map((kudo) => (
        <KudoView kudo={kudo} />
      ))}
    </ScrollView>
  );
}

function VideoParticipantColumn({
  videoInfo,
  identity,
  currentStepIndex,
  isReady,
}: {
  videoInfo: NonNullable<VideoInfoType>;
  identity: "teacher" | "learner";
  currentStepIndex: number;
  isReady: boolean;
}) {
  const dispatch = useDispatch();
  return (
    <View
      style={{
        borderRadius: 10,
        borderColor: skaleLightGray,
        borderWidth: 1,
        width: "20%",
        minWidth: 230,
      }}
    >
      <VideoUserCell
        videoInfo={videoInfo}
        identity={identity}
        callback={() => {
          dispatch(ActionCreators.setVideoInfo(null));
        }}
      />
      <View
        style={{
          borderBottomColor: skaleLightGray,
          borderBottomWidth: 1,
        }}
      />
      <VideoUserSteps
        identity={identity}
        videoInfo={videoInfo}
        currentStepIndex={currentStepIndex}
        isReady={isReady}
      />
    </View>
  );
}
